import { useRef } from 'react'
import { distanceTo } from '../lib/geometry'
import { GRID_SIZE } from '../config/constants'

// Shared path-following hook for NPC approach and monster chase.
// getWaypoint returns { waypoint, done } each frame:
//   waypoint — next step toward target, or null when path exhausted
//   done     — true when within arrivalThreshold of finalTarget (trigger dialog / arrival action)
// reportMoveResult(bool) tracks stuck counter so the hook recomputes on stall.
export const usePursuit = ({ findPath, width, height, stride = 1 }) => {
  const pathRef             = useRef([])
  const pathIdxRef          = useRef(0)
  const stuckRef            = useRef(0)
  const recomputeCounterRef = useRef(0)

  const resetPursuit = () => {
    pathRef.current = []
    pathIdxRef.current = 0
    stuckRef.current = 0
    recomputeCounterRef.current = 0
  }

  // options: { recomputeInterval?, stuckThreshold?, arrivalThreshold?, finalTarget? }
  const getWaypoint = (pos, target, options = {}) => {
    const {
      recomputeInterval = Infinity,
      stuckThreshold    = Infinity,
      arrivalThreshold  = 0,
      finalTarget,
    } = options

    if (arrivalThreshold > 0 && distanceTo(pos, finalTarget ?? target) <= arrivalThreshold) {
      return { waypoint: null, done: true }
    }

    recomputeCounterRef.current++
    const needsRecompute =
      pathRef.current.length === 0 ||
      pathIdxRef.current >= pathRef.current.length ||
      recomputeCounterRef.current >= recomputeInterval ||
      stuckRef.current > stuckThreshold

    if (needsRecompute) {
      const newPath = findPath(pos, target, width, height)
      pathRef.current = newPath
      recomputeCounterRef.current = 0
      stuckRef.current = 0
      const firstNode = newPath[0]
      pathIdxRef.current = (firstNode &&
        Math.floor(firstNode.x / GRID_SIZE) === Math.floor(pos.x / GRID_SIZE) &&
        Math.floor(firstNode.y / GRID_SIZE) === Math.floor(pos.y / GRID_SIZE)) ? 1 : 0
    }

    let waypoint = pathRef.current[pathIdxRef.current]
    while (waypoint && distanceTo(pos, waypoint) <= stride + 1) {
      pathIdxRef.current++
      waypoint = pathRef.current[pathIdxRef.current]
    }

    return { waypoint: waypoint ?? null, done: false }
  }

  const reportMoveResult = (success) => {
    if (success) stuckRef.current = 0
    else stuckRef.current++
  }

  return { resetPursuit, getWaypoint, reportMoveResult, stuckRef }
}
